<template>
  <FloatingPanelMain
    v-model="modelValue"
    title="History"
    :item-count="historyCount"
  >
    <template v-slot:default>
      <!-- History list container -->
      <div v-if="historyCount > 0">
        <FloatingPanelListItem
          v-for="entry in sortedHistory"
          :key="entry.id || entry.datetime"
          :clickable="false"
          :avatarText="getInitials(entry.author)"
          avatarColor="blue-grey"
          :avatarSize="28"
          :title="entry.author"
          :subtitle="formatDate(entry.datetime)"
          class="w-100"
        >
          <div class="mt-2 w-100">
            <div
              v-for="(change, idx) in entry.changes"
              :key="change.field + '-' + idx"
              class="mb-3 text-body-2"
            >
              <!-- High-contrast display of the modified field name -->
              <div class="font-weight-bold mb-1" style="color: rgb(var(--v-theme-hud-text)) !important;">
                {{ formatFieldName(change.field) }}
              </div>

              <!-- Dedicated Presentation for Creation Events -->
              <template v-if="change.field === 'system.creation'">
                <div class="d-flex align-start text-caption mt-1" style="gap: 4px;">
                  <v-icon size="14" color="primary" class="mt-1 flex-shrink-0">mdi-star-four-points-outline</v-icon>
                  <span class="text-primary font-weight-bold" style="word-break: break-word; line-height: 1.3;">
                    Entity was created
                  </span>
                </div>
              </template>

              <!-- Standard Diff Presentation (Old > New) -->
              <template v-else>
                <!-- Render Arrays (Multiselect/Relations) -->
                <div v-if="Array.isArray(change.newValue) || Array.isArray(change.oldValue)" class="d-flex flex-column gap-1">
                  <!-- Old Values (Removed) -->
                  <div v-if="resolveArrayItems(change.oldValue).length > 0" class="d-flex flex-wrap align-center gap-1 mb-1">
                    <v-icon size="14" color="error" class="flex-shrink-0 me-1">mdi-minus-box-outline</v-icon>
                    <v-chip
                      v-for="item in resolveArrayItems(change.oldValue)"
                      :key="item.id"
                      :color="item.color || 'error'"
                      variant="tonal"
                      size="x-small"
                      class="text-decoration-line-through opacity-70"
                    >
                      <v-icon v-if="item.icon" start size="10">{{ item.icon }}</v-icon>
                      {{ item.title }}
                    </v-chip>
                  </div>
                  <!-- New Values (Added) -->
                  <div v-if="resolveArrayItems(change.newValue).length > 0" class="d-flex flex-wrap align-center gap-1">
                    <v-icon size="14" color="success" class="flex-shrink-0 me-1">mdi-plus-box-outline</v-icon>
                    <v-chip
                      v-for="item in resolveArrayItems(change.newValue)"
                      :key="item.id"
                      :color="item.color || 'success'"
                      variant="flat"
                      size="x-small"
                    >
                      <v-icon v-if="item.icon" start size="10" color="white">{{ item.icon }}</v-icon>
                      <span class="text-white">{{ item.title }}</span>
                    </v-chip>
                  </div>
                  <!-- Handle Empty Arrays specifically -->
                  <div v-if="resolveArrayItems(change.newValue).length === 0 && resolveArrayItems(change.oldValue).length > 0" class="d-flex align-center text-caption text-medium-emphasis mt-1">
                    <v-icon size="14" class="me-1 opacity-50">mdi-cancel</v-icon> All items removed
                  </div>
                </div>

                <!-- Render Single Values (Text, Select, Status) -->
                <div v-else class="d-flex align-center flex-wrap gap-2">
                  <!-- Old Value -->
                  <v-chip
                    v-if="change.oldValue !== null && change.oldValue !== undefined && change.oldValue !== ''"
                    :color="resolveSingleItem(change.oldValue).color || 'error'"
                    variant="tonal"
                    size="small"
                    class="text-decoration-line-through opacity-70"
                  >
                    <v-icon v-if="resolveSingleItem(change.oldValue).icon" start size="12">{{ resolveSingleItem(change.oldValue).icon }}</v-icon>
                    {{ resolveSingleItem(change.oldValue).title }}
                    <!-- Optional Category/Type badge -->
                    <span v-if="resolveSingleItem(change.oldValue).subtitle" class="ms-1 text-uppercase text-disabled" style="font-size: 0.6rem;">
                       ({{ resolveSingleItem(change.oldValue).subtitle }})
                     </span>
                  </v-chip>
                  <span v-else class="text-caption text-error text-decoration-line-through opacity-70">(empty)</span>

                  <!-- Arrow Separator -->
                  <v-icon size="14" color="medium-emphasis" class="mx-1">mdi-arrow-right-thick</v-icon>

                  <!-- New Value -->
                  <v-chip
                    v-if="change.newValue !== null && change.newValue !== undefined && change.newValue !== ''"
                    :color="resolveSingleItem(change.newValue).color || 'success'"
                    variant="flat"
                    size="small"
                  >
                    <v-icon v-if="resolveSingleItem(change.newValue).icon" start size="12" color="white">{{ resolveSingleItem(change.newValue).icon }}</v-icon>
                    <span class="text-white">{{ resolveSingleItem(change.newValue).title }}</span>
                    <span v-if="resolveSingleItem(change.newValue).subtitle" class="ms-1 text-uppercase text-white opacity-70" style="font-size: 0.6rem;">
                       ({{ resolveSingleItem(change.newValue).subtitle }})
                     </span>
                  </v-chip>
                  <span v-else class="text-caption text-medium-emphasis font-italic">(empty)</span>
                </div>
              </template>
            </div>
          </div>
        </FloatingPanelListItem>
      </div>

      <!-- High-contrast empty state -->
      <div v-else class="text-body-2 text-center mt-8 pa-4" style="color: rgb(var(--v-theme-hud-text-secondary)) !important;">
        <v-icon size="48" style="color: rgba(var(--v-theme-hud-text-secondary), 0.35) !important;" class="mb-2">mdi-history</v-icon>
        <p>No history available.</p>
      </div>
    </template>
  </FloatingPanelMain>
</template>

<script setup lang="ts">
import { computed, inject, Ref } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useDatastoreStore } from '@/store/datastore';
import { useSelectOptions } from '@/core/composables/useSelectOptions';
import type { Entity } from '@/core/types/editor';

import FloatingPanelMain from '@/core/components/floatingPanel/FloatingPanelMain.vue';
import FloatingPanelListItem from '@/core/components/floatingPanel/FloatingPanelListItem.vue';

interface FormContext {
  entity: Ref<Entity | null>;
  entityId: Ref<string | null>;
  isLocked: Ref<boolean>;
  isSecondary: boolean;
}

interface Props {
  isSecondary?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isSecondary: false
});

const modelValue = defineModel<boolean>({ default: false });

const workspaceStore = useWorkspaceStore();
const datastoreStore = useDatastoreStore();
const formContext = inject<FormContext | null>('formContext', null);

const { entityInfoMap } = useSelectOptions();

// Resolves active entity with safe fallback in case formContext is not injected
const displayEntity = computed<Entity | null>(() => {
  if (formContext?.entity) return formContext.entity.value;
  return props.isSecondary ? workspaceStore.secondarySelectedEntity : workspaceStore.selectedEntity;
});

const history = computed<any[]>(() => displayEntity.value?.history || []);
const historyCount = computed<number>(() => history.value.length);

// Sorts history list descending by date
const sortedHistory = computed<any[]>(() => {
  return [...history.value].sort((a: any, b: any) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime());
});

const getInitials = (author: string | undefined): string => {
  if (!author) return '?';
  return author.charAt(0).toUpperCase();
};

const formatDate = (isoString: string | undefined): string => {
  if (!isoString) return '';
  const d = new Date(isoString);
  // Calculate relative time if within the last 24h
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMins / 60);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHrs < 24) return `${diffHrs}h ago`;

  return d.toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatFieldName = (field: string): string => {
  if (field === 'system.creation') return 'Creation';
  if (field.startsWith('raci.')) return `RACI: ${field.replace('raci.', '')}`;
  return field.charAt(0).toUpperCase() + field.slice(1);
};

// --- Smart Entity Resolution ---

// Attempts to resolve a single value (string UUID, boolean, or regular text) into a rich display object
const resolveSingleItem = (val: any): { id: string; title: string; color?: string; icon?: string; subtitle?: string } => {
  if (val === null || val === undefined) return { id: '', title: '(empty)' };

  if (typeof val === 'boolean') return { id: String(val), title: val ? 'True' : 'False', color: val ? 'success' : 'error' };

  if (typeof val === 'string' && val.length > 10 && val.includes('-')) {
    // Looks like a UUID or System Key, try to find the entity
    const entity = datastoreStore.allEntities.find((e: Entity) => e.id === val);
    if (entity) {
      // Lookup entity type styling
      const typeInfo = entityInfoMap.value[entity.entityType];
      let subtitle = typeInfo?.title || entity.entityType;

      // Special logic for statuses to show their category
      if (entity.entityType === 'statusTypes' || entity.system) {
        subtitle = entity.system ? entity.system.replace('_', ' ') : subtitle;
      }

      return {
        id: val,
        title: entity.title || val,
        color: entity.color || typeInfo?.color,
        icon: entity.icon || typeInfo?.icon,
        subtitle: subtitle
      };
    }
  }

  // Fallback for normal text fields (e.g. titles, markdown)
  // Truncate excessively long text (like full descriptions)
  const displayTitle = String(val).length > 60 ? String(val).substring(0, 60) + '...' : String(val);
  return { id: String(val), title: displayTitle };
};

// Resolves an array of values into rich display objects
const resolveArrayItems = (arr: any): Array<{ id: string; title: string; color?: string; icon?: string }> => {
  if (!arr || !Array.isArray(arr)) return [];
  return arr.map(resolveSingleItem);
};

</script>

<style scoped>
.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }
</style>
